// This is a template-less component that only handles uploading a file to S3.
// Subscribe to its events to know how the upload is doing.

import Component from '@glimmer/component';
import { action } from '@ember/object';
import FileUploadInfoModel from 'donor-elf-web/models/file-upload-info';

interface ComponentArgs {
  fileUploadInfo: FileUploadInfoModel;
  file: any;
  onComplete: () => void;
  onProgress?: (percentUploaded: number) => void;
  onError?: (e: any) => void;
  onAbort?: (e: any) => void;
}

export default class UploadFileComponent extends Component<ComponentArgs> {
  constructor(owner: any, args: any) {
    super(owner, args);
    this.upload();
  }

  xhr?: XMLHttpRequest;

  upload() {
    let formData = new FormData();
    formData.append('key', this.args.fileUploadInfo.key);
    formData.append('AWSAccessKeyId', this.args.fileUploadInfo.accessKey);
    formData.append('acl', 'private');
    formData.append('policy', this.args.fileUploadInfo.policy);
    formData.append('signature', this.args.fileUploadInfo.signature);
    formData.append('success_action_status', '201');
    formData.append('file', this.args.file);

    this.xhr = new XMLHttpRequest();
    this.xhr.upload.addEventListener('progress', this.handleXhrProgress, false);
    this.xhr.upload.addEventListener('load', this.handleXhrLoad, false);
    this.xhr.upload.addEventListener('error', this.handleXhrError, false);
    this.xhr.upload.addEventListener('abort', this.handleXhrAbort, false);
    this.xhr.open(
      'POST',
      `https://${this.args.fileUploadInfo.bucket}.s3.amazonaws.com`,
      true,
    );
    this.xhr.send(formData);
  }

  @action
  handleXhrProgress(e: any) {
    if (e.lengthComputable) {
      this.args.onProgress?.(Math.round((e.loaded * 100) / e.total));
    }
  }

  @action
  handleXhrLoad() {
    // even though the file has loaded, we still have to wait for the
    // XMLHttpRequest to complete
    this.waitForXhrToComplete();
  }

  @action
  waitForXhrToComplete() {
    if (this.xhr === undefined) {
      return;
    }
    if (this.xhr.readyState === 4) {
      this.args.onComplete();
    } else {
      setTimeout(() => {
        this.waitForXhrToComplete();
      }, 100);
    }
  }

  @action
  handleXhrError(e: any) {
    this.args.onError?.(e);
  }

  @action
  handleXhrAbort(e: any) {
    this.args.onAbort?.(e);
  }

  willDestroy() {
    this.xhr?.upload?.removeEventListener('progress', this.handleXhrProgress);
    this.xhr?.upload?.removeEventListener('load', this.handleXhrLoad);
    this.xhr?.upload?.removeEventListener('error', this.handleXhrError);
    this.xhr?.upload?.removeEventListener('abort', this.handleXhrAbort);
    // abort the upload if it hasn't already completed
    if (this.xhr?.readyState !== 4) {
      this.xhr?.abort();
    }
    this.xhr = undefined;
  }
}
